import * as Cookies from 'js-cookie';
import { setAuthToken, getAuthToken, setClientId } from '@/api/client';
import { authApi } from '@/api/auth';
import { apiClient } from '@/api/client';
import type { AuthTokenResponse } from '@/types';

enum Cookie {
    Refresh = 'iq_refresh',
    Token = 'iq_token',
    Client = 'client_id',
    Expiry = 'iq_token_expiry',
}

const COOKIES_EXPIRATION_DAYS = 200;
const REFRESH_SECONDS_BEFORE_EXPIRY = 60;
const CLIENT_ID_EXPIRES = new Date(2038, 0, 19);

class Authentication {
    private static instance: Authentication | null = null;

    constructor() {
        if (!Authentication.instance) {
            Authentication.instance = this;
        }
        return Authentication.instance;
    }

    initClientId() {
        let id = Cookies.get(Cookie.Client);
        if (!id) {
            id = `${Math.ceil(Math.random() * 10000) * Date.now()}`;
            Cookies.set(Cookie.Client, id, { expires: CLIENT_ID_EXPIRES });
        }
        setClientId(id);
        return this;
    }

    /** Called on app load. Checks for ?token= callback first, then falls back to
     *  cookie-based session restore, then redirects to the login provider. */
    async initAuth() {
        this.initClientId();
        await this.initialAuth();
        this.scheduleRefresh();
    }

    /** Handles the three entry paths:
     *  1. Returning from login provider with ?token= or #id_token= in the URL
     *  2. Existing session in cookies (page reload)
     *  3. No session — redirect to login provider */
    async initialAuth() {
        const dcTokenMatch = window.location.search.match(/\?token=(.*)$/);
        const hash = window.location.hash;

        if (hash?.startsWith('#id_token=') || dcTokenMatch?.length === 2) {
            const token = hash ? hash.substring('#id_token='.length) : (dcTokenMatch as RegExpMatchArray)[1];
            try {
                const response = await authApi.exchangeToken(token, Cookies.get(Cookie.Client) || '');
                this.saveTokens(response);
                window.history.replaceState({}, document.title,
                    window.location.href.replace(/(\?token=.*$)|(#id_token=.*$)/, ''));
            } catch {
                await this.redirectToLoginPage();
            }
        } else if (!Cookies.get(Cookie.Token) || !Cookies.get(Cookie.Refresh)) {
            await this.redirectToLoginPage();
        } else if (Cookies.get(Cookie.Expiry) && new Date(Cookies.get(Cookie.Expiry)!).getTime() <= Date.now()) {
            await this.refreshToken(false);
        } else {
            // Valid session in cookies — restore in-memory token for Authorization header
            setAuthToken(Cookies.get(Cookie.Token)!);
        }
    }

    async refreshToken(poll: boolean) {
        try {
            const response = await authApi.refreshToken(
                Cookies.get(Cookie.Token) || '',
                Cookies.get(Cookie.Refresh) || '',
                Cookies.get(Cookie.Client) || ''
            );
            this.saveTokens(response);
            if (poll) {
                this.scheduleRefresh();
            }
        } catch {
            setAuthToken(null);
            Cookies.remove(Cookie.Token);
            Cookies.remove(Cookie.Refresh);
            Cookies.remove(Cookie.Expiry);
            this.redirectToLoginPage();
        }
    }

    async logout(redirect = true) {
        try {
            await authApi.logout();
        } catch {
            // Best-effort
        }
        Cookies.remove(Cookie.Token);
        Cookies.remove(Cookie.Refresh);
        Cookies.remove(Cookie.Expiry);
        setAuthToken(null);

        if (redirect) {
            window.location.href = process.env.VUE_APP_LOGOUT_REDIRECT_URL || process.env.VUE_APP_LOGIN_URL || '/auth/login';
        }
    }

    getToken() {
        return getAuthToken();
    }

    isAuthenticated() {
        return !!Cookies.get(Cookie.Token);
    }

    async redirectToLoginPage() {
        try {
            const { callback_url: callbackUrl } = await apiClient.get<{ callback_url: string | null }>('auth/login-info');
            if (callbackUrl) {
                const toUrl = encodeURIComponent(
                    `${window.location.href}${window.location.href.includes('?') ? '&token=' : '?token='}`
                );
                window.location.href = `${callbackUrl}${toUrl}`;
            } else {
                window.location.href = `${process.env.VUE_APP_LOGIN_URL}${window.location.href}?token=`;
            }
        } catch {
            window.location.href = process.env.VUE_APP_LOGIN_URL || '/auth/login';
        }
    }

    private saveTokens(response: AuthTokenResponse) {
        setAuthToken(response.token);
        Cookies.set(Cookie.Token, response.token, { expires: COOKIES_EXPIRATION_DAYS });
        Cookies.set(Cookie.Refresh, response.refresh_token, { expires: COOKIES_EXPIRATION_DAYS });
        Cookies.set(Cookie.Expiry, response.expiry, { expires: COOKIES_EXPIRATION_DAYS });
    }

    private scheduleRefresh() {
        const expiry = Cookies.get(Cookie.Expiry);
        if (!expiry) return;

        const msUntilRefresh = new Date(expiry).getTime() - Date.now() - REFRESH_SECONDS_BEFORE_EXPIRY * 1000;
        if (msUntilRefresh > 0) {
            setTimeout(() => this.refreshToken(true), msUntilRefresh);
        } else {
            this.refreshToken(true);
        }
    }
}

const instance = new Authentication();
Object.freeze(instance);
export default instance;
